import { useCallback } from 'react';
import { navigateBack } from '@helpers';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { trackSearchResultTapped } from '@services/tracking';
import { buildAttachPayload } from '@components/search/operations/helpers';

export interface UseManageOperationsParams {
  isPopupMode?: boolean;
  attachMode?: boolean;
  attachImageId?: number;
  onItemSelect?: (item: any, type: string, levelSubScreen: LevelSubScreen) => void;
  updateImageMetadata: (data: any) => void;
}

export const useManageOperations = ({
  isPopupMode,
  attachMode,
  attachImageId,
  onItemSelect,
  updateImageMetadata,
}: UseManageOperationsParams) => {
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const goToDetails = useCallback(
    (item: any, levelSubScreen: LevelSubScreen = LevelSubScreen.GENERAL) => {
      navigateToMainDetailsScreen({
        mainScreen: JobDetailsScreenName,
        subScreen: JobDetailsScreen.JOBS,
        levelSubScreen,
        params: {
          perspective: Perspective.JOBS,
          productionOperationId: item.productionOperationId,
          selectedImageKey: item.imageHash,
          documentId: item.imageDocumentId,
        },
      });
    },
    [navigateToMainDetailsScreen],
  );

  const attachItemToImage = useCallback(
    (item: any) => {
      if (!attachImageId) return;
      const backendData = buildAttachPayload(item, attachImageId);
      updateImageMetadata(backendData);
      navigateBack();
    },
    [attachImageId, updateImageMetadata],
  );

  const open = useCallback(
    (item: any, levelSubScreen: LevelSubScreen = LevelSubScreen.GENERAL) => {
      trackSearchResultTapped('ProductionOperation');

      if (isPopupMode && onItemSelect) {
        onItemSelect(item, 'Operation', levelSubScreen);
        return;
      }

      if (attachMode && attachImageId) {
        attachItemToImage(item);
        return;
      }

      goToDetails(item, levelSubScreen);
    },
    [isPopupMode, onItemSelect, attachMode, attachImageId, attachItemToImage, goToDetails],
  );

  return { open, goToDetails, attachItemToImage };
};
